import React, {
  FC,
  useState,
  ReactElement,
  ChangeEvent,
  useEffect,
} from 'react';
import { FormControl, OutlinedInput, InputAdornment } from '@mui/material';
import { makeStyles } from '@mui/styles';
import SearchIcon from '@mui/icons-material/Search';

import {
  useImportData,
  useSearch,
  useSearchTree,
  useOrganization,
  useChangeKarmaUser,
  useSubdivivsion,
} from '../../hooks/entityHooks';

import classes from './OrgStructure.module.scss';
import { CustomizedTreeView } from '../../Components/DnDTree';
import { EmloyeeInfo } from '../../Components/EmployeeInfo';
import { OrgInfo } from '../../Components/OrganizationInfo';
import { EmployeeLogs } from '../../Components/EmployeeLogs';
import { ConfigDialog } from '../../Components/ConfigDialog';
import { ChangeUserDialog } from '../../Components/Dialogs/ChangeEmployeeDialog';
import { ChangeOrgDialog } from '../../Components/Dialogs/ChangeOrgDialog';

import { LineLoader } from '../../Components/LineLoader';

import { ReactComponent as ClearSearch } from '../../assets/images/clearSearch.svg';
import download from '../../assets/images/download.png';

import { SearchResult } from '../../Components/SearchResult';
import { ThanksBlock } from '../../Components/ThanksBlock';

import { Subdivision } from '../../types/subdivision';
import { Organization } from '../../types/organization';
import AccessBlock from '../../Components/AccessBlock';
import { TypeModalWindow } from '../../types/utils/constants';

const useStyles = makeStyles({
  root: {
    width: '77%',
    fontFamily: 'HelveticaNeueCyr',
    background: '#FFFFFF',
    '&::placeholder': {
      color: '#CACED2',
    },
    // borderColor: '#EB564F',
    '&.Mui-focused': {
      border: '1px solid #FF7221',
      color: '#FF7221',
    },
    '&.MuiOutlinedInput': {
      borderColor: '#FF7221',
    },
  },
  emptyInput: {
    maxHeight: 44,
    borderColor: '#CACED2',
    '&.Mui-focused': {
      borderColor: '#FF7221',
      // color: '#EB564F',
    },
  },
  input: {
    maxHeight: 44,
    borderColor: '#FF7221',
    '&.Mui-focused': {
      borderColor: '#FF7221',
      // color: '#EB564F',
    },
  },
});

export type orgProps = {
  type: string;
};

export type HandleOrg = {
  id: number;
  type: string;
  handleGetTree?(): void;
};

export type ChangeOptions = {
  a: string;
  b: string;
  newParent: number;
  branch: { from: number[]; to: number[] };
  tree?: Subdivision;
  fromOrg: Organization;
  toOrg: Organization;
  id: number;
  isStuff: boolean;
};

export type logsInfo = {
  id?: number;
  type?: number;
  handleGetTree?(): void;
};

export type SearchParams = {
  staff: {
    page: number;
    isTrue: boolean;
  };
  org: {
    page: number;
    isTrue: boolean;
  };
  sub: {
    page: number;
    isTrue: boolean;
  };
};

export type UpdateEntity = {
  id: number;
  getTree(): void;
  type: number;
};

export type UpdateEntityArray = UpdateEntity[];

export const OrgStructure: FC = (): ReactElement => {
  const [value, setValue] = useState('');
  const [searchClicked, setSearchClicked] = useState(false);
  const [entityLoader, setEntityLoader] = useState(false);

  const defaultSearchResult = {
    data: {
      organizations: { organizations: [], totalCount: 1, totalPages: 1 },
      staffs: { staffs: [], totalCount: 1, totalPages: 1 },
      subdivisions: { subdivisions: [], totalCount: 1, totalPages: 1 },
    },
  };
  const defaultSearchParams = {
    staff: {
      page: 1,
      isTrue: false,
    },
    org: {
      page: 1,
      isTrue: false,
    },
    sub: {
      page: 1,
      isTrue: false,
    },
  };
  const [searchParams, setSearchParams] = useState<SearchParams>(
    defaultSearchParams,
  );

  const handleSetEntityLoader = (val: boolean) => {
    setEntityLoader(val);
  };
  if (!value.length && searchClicked) {
    setSearchClicked(false);
  }

  const handleSearchClick = () => {
    setSearchClicked(true);
  };
  const handleClearSearch = () => {
    setValue('');
    clearSearchResult();
    setSearchParams({
      staff: {
        page: 1,
        isTrue: false,
      },
      org: {
        page: 1,
        isTrue: false,
      },
      sub: {
        page: 1,
        isTrue: false,
      },
    });

    // searchUpdate();
  };
  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);

    clearSearchResult();
    setSearchParams({
      staff: {
        page: 1,
        isTrue: false,
      },
      org: {
        page: 1,
        isTrue: false,
      },
      sub: {
        page: 1,
        isTrue: false,
      },
    });
    if (searchClicked) {
      setSearchClicked(false);
    }
    if (!value.length) {
      clearSearchResult();
      setSearchParams({
        staff: {
          page: 1,
          isTrue: false,
        },
        org: {
          page: 1,
          isTrue: false,
        },
        sub: {
          page: 1,
          isTrue: false,
        },
      });
    }

    // searchUpdate();
  };
  const {
    getEntityList,
    entityList,
    initLoading,
    clearEntityList,
    getEntity,
  } = useOrganization();
  const { changeEntity } = useChangeKarmaUser();
  const { importer } = useImportData();
  const {
    search,
    searchResult,
    initLoading: searchLoading,
    clearSearchResult,
  } = useSearch();

  const {
    entity: searchTree,
    getSearchOrgTree,
    getSearchStaffTree,
    getSearchSubTree,
  } = useSearchTree();

  useEffect(() => {
    if (!value.length) {
      getEntityList();
    }
  }, [value]);

  useEffect(() => {
    if (value.length > 2) {
      if (searchParams.staff.isTrue) {
        const stringParams = `filter[text]=${value}&filter[pagination][per_page]=10&filter[pagination][page]=${searchParams.staff.page}&filter[type_entity]=3`;
        const str = new URLSearchParams(stringParams);
        search(str);
      } else if (searchParams.sub.isTrue) {
        const stringParams = `filter[text]=${value}&filter[pagination][per_page]=10&filter[pagination][page]=${searchParams.sub.page}&filter[type_entity]=2`;
        const str = new URLSearchParams(stringParams);
        search(str);
      } else if (searchParams.org.isTrue) {
        const stringParams = `filter[text]=${value}&filter[pagination][per_page]=10&filter[pagination][page]=${searchParams.org.page}&filter[type_entity]=1`;
        const str = new URLSearchParams(stringParams);
        search(str);
      } else {
        const stringParams = `filter[text]=${value}&filter[pagination][per_page]=10&filter[pagination][page]=1`;
        const str = new URLSearchParams(stringParams);
        search(str);
      }
    }
  }, [value, searchParams]);

  const orgArr = entityList;

  const [changedEntity, setChangedEnity] = useState();

  const [employeeInfoShow, setEmployeeInfoShow] = useState(false);
  const [orgInfoShow, setOrgInfoShow] = useState(false);
  const [logs, setLogs] = useState(false);
  const [config, setConfig] = useState({
    state: false,
    type: TypeModalWindow.ORGANIZATION_DIALOG,
  });
  const [open, setOpen] = useState(false);

  const [openChange, setOpenChange] = useState(false);
  const [changeOptions, setChangeOptions] = useState({
    a: '',
    b: '',
    newParent: 0,
    tree: {} as Subdivision | undefined,
    branch: [] as any,
    fromOrg: {} as Organization,
    toOrg: {} as Organization,
    id: 0,
    isStuff: false,
  });

  const [userInfo, setUserIfo] = useState<logsInfo>({});
  const [organizationInfo, setOrganizationfo] = useState<HandleOrg>({
    type: '',
    id: 1,
  });
  const handleOrganizationInfo = ({ id, type, handleGetTree }: HandleOrg) => {
    setOrganizationfo({ id, type, handleGetTree });
  };
  const handleSetStaff = ({ id, type, handleGetTree }: logsInfo) => {
    setUserIfo({ id, type, handleGetTree });
  };

  const handleChangeClose = () => {
    setOpenChange(!openChange);
  };

  const handleChangeOptions = ({
    a,
    b,
    newParent,
    tree,
    branch,
    fromOrg,
    toOrg,
    id,
    isStuff,
  }: ChangeOptions) => {
    const newOption = {
      a,
      b,
      newParent,
      id,
      tree,
      branch,
      fromOrg,
      toOrg,
      isStuff,
    };
    setChangeOptions(newOption);
    handleChangeClose();
  };

  const handleChangeIsKarmaUser = async (
    id: number,
    type: number,
    value: boolean,
    handleGetTree: () => void,
  ) => {
    await changeEntity(id, type, value).then(() => handleGetTree());
  };

  const [updateEntityArray, setUpdateEntityArray] = useState<UpdateEntityArray>(
    [],
  );

  const handleSetEntityUpdateArray = ({ getTree, id, type }: UpdateEntity) => {
    const newArray = updateEntityArray;
    if (!newArray.find((el) => el.id === id && el.type === type)) {
      newArray.push({ id, getTree, type });
    }

    setUpdateEntityArray(newArray);
  };
  const handleUpdateEntity = async (entityId: number, type: number) => {
    const currentElement = updateEntityArray?.find(
      (el) => el.id === entityId && el.type === type,
    );

    if (currentElement) {
      await currentElement.getTree();
    }
  };

  const handleMoveEntity = async (
    subdivisions: number[],
    organizations: number[],
  ) => {
    updateEntityArray.forEach((entity) => {
      if (
        (entity.type === 1 && organizations.includes(entity.id)) ||
        (entity.type === 2 && subdivisions.includes(entity.id))
      ) {
        entity.getTree();
      }
    });
  };

  const style = useStyles();
  return (
    <>
      <>
        <div className={classes.section}>
          <div
            className={
              logs || config.state
                ? `${classes.body} + ${classes.covered}`
                : classes.body
            }
          >
            <div className={classes.body__container}>
              <div className={classes.body__container__search}>
                <FormControl className={style.root}>
                  <OutlinedInput
                    className={style.input}
                    placeholder="Введите ФИО сотрудника"
                    value={value}
                    onChange={handleChange}
                    startAdornment={
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    }
                    endAdornment={
                      <InputAdornment position="end">
                        <ClearSearch
                          onClick={handleClearSearch}
                          style={{ cursor: 'pointer' }}
                        />
                      </InputAdornment>
                    }
                  />
                </FormControl>
                <div
                  className={classes.body__container__search__downLoad}
                  onClick={() => importer()}
                >
                  <div
                    className={classes.body__container__search__downLoad__image}
                  >
                    <img src={download} alt="" />
                  </div>
                  <p>Загрузить список сотрудников</p>
                </div>
              </div>
              {!searchClicked ? (
                value.length > 2 ? (
                  <SearchResult
                    searchResult={searchResult}
                    searchString={value}
                    searchLoading={searchLoading}
                    handleSearchClick={handleSearchClick}
                    getSearchOrgTree={getSearchOrgTree}
                    getSearchStaffTree={getSearchStaffTree}
                    getSearchSubTree={getSearchSubTree}
                    handleSetStaff={handleSetStaff}
                    setEmployeeInfoShow={setEmployeeInfoShow}
                    setOrgInfoShow={setOrgInfoShow}
                    handleOrganizationInfo={handleOrganizationInfo}
                    clearSearchResult={clearSearchResult}
                    setSearchParams={setSearchParams}
                    searchParams={searchParams}
                  />
                ) : (
                  <>
                    {entityLoader ? <LineLoader /> : null}

                    <div className={classes.body__container__main}>
                      <div className={classes.body__container__main__structure}>
                        <CustomizedTreeView
                          orgArr={orgArr}
                          setEmployeeInfoShow={setEmployeeInfoShow}
                          setOrgInfoShow={setOrgInfoShow}
                          handleSetStaff={handleSetStaff}
                          handleOrganizationInfo={handleOrganizationInfo}
                          handleChangeOptions={handleChangeOptions}
                          entityLoader={entityLoader}
                          handleSetEntityLoader={handleSetEntityLoader}
                          handleChangeIsKarmaUser={handleChangeIsKarmaUser}
                          handleSetEntityUpdateArray={
                            handleSetEntityUpdateArray
                          }
                          getEntityList={getEntityList}
                          // handleGetTree={handleGetTree}
                        />
                      </div>
                      <div className={classes.body__container__main__info}>
                        {!employeeInfoShow && orgInfoShow ? (
                          <OrgInfo
                            setConfig={setConfig}
                            organizationInfo={organizationInfo}
                            setLogs={setLogs}
                            handleChangeIsKarmaUser={handleChangeIsKarmaUser}
                            handleUpdateEntity={handleUpdateEntity}
                          />
                        ) : null}
                        {employeeInfoShow && !orgInfoShow ? (
                          <EmloyeeInfo
                            setConfig={setConfig}
                            setLogs={setLogs}
                            open={open}
                            setOpen={setOpen}
                            logsInfo={userInfo}
                            handleChangeIsKarmaUser={handleChangeIsKarmaUser}
                            handleUpdateEntity={handleUpdateEntity}
                          />
                        ) : null}
                      </div>
                    </div>
                  </>
                )
              ) : searchTree ? (
                <>
                  {entityLoader ? <LineLoader /> : null}
                  <div className={classes.body__container__main}>
                    <div className={classes.body__container__main__structure}>
                      <CustomizedTreeView
                        orgArr={orgArr}
                        setEmployeeInfoShow={setEmployeeInfoShow}
                        setOrgInfoShow={setOrgInfoShow}
                        handleSetStaff={handleSetStaff}
                        handleOrganizationInfo={handleOrganizationInfo}
                        handleChangeOptions={handleChangeOptions}
                        searchTree={searchTree}
                        entityLoader={entityLoader}
                        handleSetEntityLoader={handleSetEntityLoader}
                        handleChangeIsKarmaUser={handleChangeIsKarmaUser}
                        handleSetEntityUpdateArray={handleSetEntityUpdateArray}
                        getEntityList={getEntityList}
                      />
                    </div>
                    <div className={classes.body__container__main__info}>
                      {!employeeInfoShow && orgInfoShow ? (
                        <OrgInfo
                          organizationInfo={organizationInfo}
                          setLogs={setLogs}
                          handleChangeIsKarmaUser={handleChangeIsKarmaUser}
                          handleUpdateEntity={handleUpdateEntity}
                          setConfig={setConfig}
                        />
                      ) : null}
                      {employeeInfoShow && !orgInfoShow ? (
                        <EmloyeeInfo
                          setLogs={setLogs}
                          setConfig={setConfig}
                          open={open}
                          setOpen={setOpen}
                          logsInfo={userInfo}
                          handleChangeIsKarmaUser={handleChangeIsKarmaUser}
                          handleUpdateEntity={handleUpdateEntity}
                        />
                      ) : null}
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          </div>
          {logs ? (
            <EmployeeLogs
              setLogs={setLogs}
              logsInfo={userInfo}
              handleUpdateEntity={handleUpdateEntity}
            />
          ) : null}
          {config.state && config.type === TypeModalWindow.CONFIG_DIALOG ? (
            <ConfigDialog setConfig={setConfig} config={config} />
          ) : null}

          {config.state && config.type === TypeModalWindow.THANKS_DIALOG ? (
            <ThanksBlock
              setConfig={setConfig}
              config={config}
              id={userInfo.id}
            />
          ) : null}

          {config.state &&
          config.type === TypeModalWindow.ACCESS_DIALOG &&
          userInfo.id ? (
            <AccessBlock
              id={userInfo.id}
              setConfig={setConfig}
              config={config}
            />
          ) : null}

          {userInfo ? (
            <ChangeUserDialog
              open={open}
              setOpen={setOpen}
              logsInfo={userInfo}
              handleUpdateEntity={handleUpdateEntity}
            />
          ) : null}
        </div>
        <ChangeOrgDialog
          open={openChange}
          handleClose={handleChangeClose}
          points={changeOptions}
          handleUpdateEntity={handleUpdateEntity}
          moveEntity={handleMoveEntity}
        />
      </>
    </>
  );
};
